import { useTu } from "@src/appV2/i18n/useTu";
import { SelectableOptionList } from "@src/appV2/redesign/components/SelectableOptionList/SelectableOptionList";
import { Text } from "@src/appV2/redesign/components/Text";
import { type TimeSlotWithExtras } from "@src/appV2/Shifts/types";
import { APPLY_TO_OPTIONS, type ApplyToOption } from "./types";

interface ApplyToOptionsProps {
  selectedOption: ApplyToOption;
  onOptionChange: (option: ApplyToOption) => void;
  showAllShiftsOption?: boolean;
  /**
   * When false, hides the qualification-specific "Set as default for new
   * {qualification} {slot} shifts" option (THIS_AND_FOLLOWING). The backend only
   * honors a per-qualification default-time update when the
   * qualification-specific-shift-times flag is enabled for the workplace;
   * offering the option otherwise silently writes the global default for all
   * qualifications.
   */
  showQualificationSpecificOption?: boolean;
  timeSlot: TimeSlotWithExtras;
  qualificationName?: string;
}

interface OptionConfig {
  value: ApplyToOption;
  label: string;
}

function getTimeSlotLabel(timeSlot: TimeSlotWithExtras): string {
  return timeSlot.toUpperCase();
}

function getOptions(
  timeSlot: TimeSlotWithExtras,
  tu: (defaultText: string, options?: { values?: Record<string, string> }) => string,
  qualificationName?: string
): OptionConfig[] {
  const slotLabel = getTimeSlotLabel(timeSlot);
  const qualificationPrefix = qualificationName ? `${qualificationName} ` : "";

  return [
    {
      value: APPLY_TO_OPTIONS.THIS_SHIFT_ONLY,
      label: tu("Just to this shift"),
    },
    {
      value: APPLY_TO_OPTIONS.THIS_AND_FOLLOWING,
      label: tu("Set as default for new {{shiftGroup}} shifts", {
        values: { shiftGroup: `${qualificationPrefix}${slotLabel}` },
      }),
    },
    {
      value: APPLY_TO_OPTIONS.ALL_SHIFTS,
      label: tu("Set as default for all new {{slotLabel}} shifts", { values: { slotLabel } }),
    },
  ];
}

export function ApplyToOptions(props: ApplyToOptionsProps) {
  const { tu } = useTu("schedule");

  const {
    selectedOption,
    onOptionChange,
    showAllShiftsOption = true,
    showQualificationSpecificOption = true,
    timeSlot,
    qualificationName,
  } = props;

  const isOrientation = timeSlot === "ori";
  const options = getOptions(timeSlot, tu, qualificationName);

  const visibleOptions = options.filter((option) => {
    if (!showAllShiftsOption && option.value === APPLY_TO_OPTIONS.ALL_SHIFTS) {
      return false;
    }
    if (
      (isOrientation || !showQualificationSpecificOption) &&
      option.value === APPLY_TO_OPTIONS.THIS_AND_FOLLOWING
    ) {
      return false;
    }
    return true;
  });

  return (
    <SelectableOptionList
      header={
        <Text color="text.secondary" variant="caption">
          {tu("How do you want to apply this time change?")}
        </Text>
      }
      options={visibleOptions}
      selectedValue={selectedOption}
      onSelect={onOptionChange}
    />
  );
}
